import * as React from 'react';
import { useWindowSize } from 'react-use';
import { Box, useTheme } from '@mui/material';

import { EnhancedTable, ZoneCardList } from 'components';
import { useActiveZonesTableRows } from 'pages/zones/zones.hook';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { ZoneCopyDialog, ZoneDeactivateDialog, ZoneServiceItemsDialog, ZoneUpdateDialog } from 'dialogs/zone';
import { updateLayout } from 'store/slices/layout.slice';
import { convertZonesToTableData } from 'utils';
import { useOutletContext } from 'react-router-dom';
import { IZone } from 'models';
import { retainSelectedBuildingZoneIds } from './building-zone-decals';

type State = {
  zoneId: number | null;
  categoryId: number | null;
  visibleZoneServiceItemsDialog: boolean;
  visibleZoneUpdateDialog: boolean;
  visibleZoneDeactivateDialog: boolean;
  visibleZoneCopyDialog: boolean;
};

type ZonesContextType = {
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  zones: IZone[];
  loading: boolean;
};

export const BuildingZonesActivePage = () => {
  const dispatch = useAppDispatch();
  const { isAdmin } = useAppSelector(selectCurrentUserInfo);
  const theme = useTheme();
  const { width } = useWindowSize();
  const [
    {
      zoneId,
      categoryId,
      visibleZoneServiceItemsDialog,
      visibleZoneUpdateDialog,
      visibleZoneDeactivateDialog,
      visibleZoneCopyDialog,
    },
    setState,
  ] = React.useState<State>({
    zoneId: null,
    categoryId: null,
    visibleZoneServiceItemsDialog: false,
    visibleZoneUpdateDialog: false,
    visibleZoneDeactivateDialog: false,
    visibleZoneCopyDialog: false,
  });

  const { selected, setSelected, zones, loading } = useOutletContext<ZonesContextType>();

  const [, columnKeys, columns, rows] = useActiveZonesTableRows({
    setState,
    loading,
    zones,
  });

  React.useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Building Details' }));
  }, []);

  const handleFilteredDataChange = React.useCallback(
    (filteredZones: IZone[]) => {
      setSelected((currentSelected) => {
        const retainedSelected = retainSelectedBuildingZoneIds(currentSelected, filteredZones);

        return retainedSelected.length === currentSelected.length ? currentSelected : retainedSelected;
      });
    },
    [setSelected],
  );

  return (
    <>
      {width < theme.breakpoints.values.sm ? (
        <Box sx={{ paddingTop: 2 }}>
          <ZoneCardList
            zones={rows.sort((a, b) => (a.floorInfo > b.floorInfo ? 1 : a.floorInfo < b.floorInfo ? -1 : 0))}
            noDataLabel="No active zone"
          />
        </Box>
      ) : (
        <EnhancedTable
          config={{ toolbar: false, rowCheckable: true }}
          loading={loading}
          loadingLabel={'Loading active zones'}
          noDataLabel={'No active zones'}
          data={rows}
          convertFunctionForDataToTableData={convertZonesToTableData(isAdmin)}
          headColumns={columns}
          columnKeys={columnKeys}
          defaultSortColumnName="floorInfo"
          defaultSortDirection="asc"
          styles={{ tableStyle: { minWidth: 0 } }}
          initialRowsPerPage={10}
          visiblePagination
          rowActions={{}}
          selected={selected}
          setSelected={setSelected}
          onFilteredDataChange={handleFilteredDataChange}
        />
      )}

      {typeof zoneId === 'number' && (
        <>
          <ZoneServiceItemsDialog
            visible={visibleZoneServiceItemsDialog}
            setVisible={(visibleZoneServiceItemsDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneServiceItemsDialog }))
            }
            zoneId={zoneId}
            categoryId={categoryId}
          />

          <ZoneUpdateDialog
            visible={visibleZoneUpdateDialog}
            setVisible={(visibleZoneUpdateDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneUpdateDialog }))
            }
            zoneId={zoneId}
          />

          <ZoneCopyDialog
            visible={visibleZoneCopyDialog}
            setVisible={(visibleZoneCopyDialog: boolean) => setState((prev) => ({ ...prev, visibleZoneCopyDialog }))}
            zoneId={zoneId}
          />

          <ZoneDeactivateDialog
            visible={visibleZoneDeactivateDialog}
            setVisible={(visibleZoneDeactivateDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneDeactivateDialog }))
            }
            zoneId={zoneId}
          />
        </>
      )}
    </>
  );
};
